import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaskPhone } from '@config/mask.config';
import {
  RegexEmail,
  RegexOnlyLetters,
  RegexPassword,
  RegexPhone,
} from '@config/regex.config';
import {
  SignupResponse,
  SignupResponseModel,
  SocialLoginEnum,
  TypeDataInProgress,
} from '@models';
import { Subscription } from 'rxjs';
import { LoginService } from 'src/app/pages/session/login/login.service';
import { ScheduleVisitService } from '../schedule-visit.service';

@Component({
  selector: 'ggi-journey-request-visit',
  templateUrl: './request-visit.component.html',
  styleUrls: ['./request-visit.component.scss'],
})
export class RequestVisitComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isPropertyFlow = false;
  @Input() tryContinue = 0;
  @Output() canUserContinue = new EventEmitter<boolean>();
  @Output() setSignupResponse = new EventEmitter<SignupResponse>();
  @Output() formPass = new EventEmitter<any>();

  public form: FormGroup;

  public confirmPassword: string;
  public showPassword = false;
  public showPasswordConfirm = false;
  public isProspect = true;
  public changeEmail: boolean;

  public subscriptions: Subscription[] = [];

  public readonly MaskPhone = MaskPhone;

  public SocialLoginEnum = SocialLoginEnum;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: ScheduleVisitService,
    private readonly loginService: LoginService
  ) {}

  public ngOnInit(): void {
    this.generateForm();
  }

  public ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    if (
      changes.tryContinue?.previousValue !== changes.tryContinue?.currentValue
    ) {
      this.form?.markAllAsTouched();
    }
  }

  /**
   * @description Sign in with social media option
   * @param provider Social Network type
   */
  public signInWithSocialMedia(provider: SocialLoginEnum): void {
    this.subscriptions.push(
      this.loginService
        .signWithSocialMedia(provider)
        .subscribe(({ uid, accessToken, profile }) => {
          const { email, name } = profile;

          this.service.saveDataInProgress(
            {
              accessToken,
              uid,
              firstName: name,
              email,
              originAccount: provider,
            },
            TypeDataInProgress.newAccount
          );
          const request = this.service.getSignUpWithScheduleVisitData();
          if (!request.interests) {
            request.interests = [];
          }

          this.subscriptions.push(
            this.service
              .signInWithSocialMedia(request)
              .subscribe((res: SignupResponseModel) => {
                const { response } = res;
                this.setSignupResponse.emit(response);
              })
          );
        })
    );
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  /**
   * @description Init forms by section
   */
  public generateForm(): void {
    this.changeEmail = true;
    if (this.isProspect) {
      this.form = this.fb.group({
        email: ['', [Validators.required, Validators.pattern(RegexEmail)]],
        acceptPoliciesAndTerms: [false, Validators.requiredTrue],
      });
      this.canUserContinue.next(false);
      this.initChangeSubscription();
      this.changeEmail = false;
      return;
    }
    this.form = this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexOnlyLetters),
          Validators.maxLength(50),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexOnlyLetters),
          Validators.maxLength(50),
        ],
      ],
      phone: ['', [Validators.required, Validators.pattern(RegexPhone)]],
      email: ['', [Validators.required, Validators.pattern(RegexEmail)]],
      password: ['', [Validators.required, Validators.pattern(RegexPassword)]],
      confirmPassword: [
        '',
        [Validators.required, Validators.pattern(RegexPassword)],
      ],
      acceptPoliciesAndTerms: [false, Validators.requiredTrue],
    });
    this.canUserContinue.next(false);
    this.initChangeSubscription();
    this.changeEmail = false;
  }

  /**
   * @description Show and hide password text by section
   * @param confirm True if confirm password, False in password
   */
  public changePasswordType(confirm?: string): void {
    if (confirm) {
      this.showPasswordConfirm = !this.showPasswordConfirm;
    } else {
      this.showPassword = !this.showPassword;
    }
  }

  /**
   * @description Open in new window privacy terms
   */
  public openTerms(): void {
    window.open('/assets/pdf/privacy-smart-walk.pdf', '_blank');
  }

  /**
   * @description Subscribe to form changes
   */
  private initChangeSubscription(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        const { valid, value } = this.form;

        this.formPass.next(value);

        if (valid) {
          this.service.saveDataInProgress(value, TypeDataInProgress.newAccount);
        }

        this.canUserContinue.next(valid);
      })
    );
  }
}
